import classnames from 'classnames/bind';

import { NavRoute } from 'application/types/routes';

import { useCopilot } from 'context/copilot';
import { useAccount } from 'hooks/api/useAccount';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useHasAccess } from 'hooks/useHasAccess';
import { useIsLumeoAdmin } from 'hooks/useAuth';
import { useModal } from 'hooks/useModal';
import { showGleap } from 'services/gleap';

import { CreateButton } from 'components/CreateButton/CreateButton';
import { Icon } from 'components/Icon/IconDeprecated';

import { ApplicationSelect } from './components/ApplicationSelect';
import { NavUserMenu } from './components/NavUserMenu';
import { PrimaryLink } from './PrimaryLink';
import styles from './Nav.module.scss';

const c = classnames.bind(styles);

type NavProps = {
  className?: string;
};

export function Nav({ className }: NavProps) {
  const [hasAccess] = useHasAccess();
  const isLumeoAdmin = useIsLumeoAdmin();
  const { open } = useModal();
  const { isAvailable } = useCopilot();
  const { isLumeoDomainAccount } = useAccount();
  const { data: organization } = useCurrentOrganization();

  const showAskAI = isAvailable && isLumeoDomainAccount;
  const isSuspended = Boolean(organization?.is_suspended);

  function handleAskAIClick() {
    open('copilot-chat');
  }

  return (
    <div className={c('nav', className)}>
      <div className={c('nav-top')}>
        <ApplicationSelect />

        <div className={c('secondary-links')}>
          {showAskAI && (
            <button
              className={c('secondary-link', 'ask-ai')}
              onClick={handleAskAIClick}
            >
              <Icon name="logo" size="small" />
              <span>Ask AI</span>
            </button>
          )}
          <a
            className={c('secondary-link')}
            href="https://docs.lumeo.com"
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="documentation" size="small" />
            <span>Docs</span>
          </a>
          <button
            className={c('secondary-link')}
            onClick={showGleap}
            type="button"
          >
            <Icon name="help" size="small" />
            <span>Help</span>
          </button>
          <NavUserMenu />
        </div>
      </div>
      <div className={c('nav-bottom')}>
        <ul className={c('primary-links')}>
          {hasAccess('overview') && (
            <PrimaryLink
              route={NavRoute.OVERVIEW}
              icon="dashboard"
              disabled={isSuspended}
              end
            >
              Overview
            </PrimaryLink>
          )}

          {hasAccess('design') && (
            <PrimaryLink
              route={NavRoute.DESIGN}
              icon="pipeline"
              disabled={isSuspended}
            >
              Analytics
            </PrimaryLink>
          )}

          {hasAccess('deploy_edit') && (
            <PrimaryLink
              route={NavRoute.DEPLOY}
              icon="deployment"
              disabled={isSuspended}
            >
              Deploy
            </PrimaryLink>
          )}

          {hasAccess('monitor') && (
            <PrimaryLink
              route={NavRoute.MONITOR}
              icon="event"
              disabled={isSuspended}
            >
              Monitor
            </PrimaryLink>
          )}

          <PrimaryLink route={NavRoute.SETTINGS} icon="settings">
            Settings
          </PrimaryLink>

          {isLumeoAdmin && (
            <PrimaryLink route={NavRoute.ADMIN} icon="account-key">
              Admin
            </PrimaryLink>
          )}
        </ul>

        <CreateButton disabled={isSuspended} />
      </div>
    </div>
  );
}
